import { SvelteSet } from 'svelte/reactivity'

import type { IClassificationRunArgs, IODRunArgs } from '@routes/data-qa/runs/dataset-qa-runs'

const DEFAULT_IMAGE_SIZE = [224, 224] as readonly [number, number]
const DEFAULT_RADAR_SIZE = [0, 0] as readonly [number, number]
const DEFAULT_UPSAMPLE = false

type ArrayKeys<T> = {
  [K in keyof T]: T[K] extends unknown[] ? K : never
}[keyof T]

abstract class RunArgs<T extends Record<string, unknown>> {
  protected abstract defaultValues: T
  protected abstract data: T
  changedFields: SvelteSet<keyof T> = $state(new SvelteSet())

  updateChangedFields(field: keyof T, newValue: unknown) {
    if (!this.changedFields.has(field)) {
      this.changedFields.add(field)
    } else if (newValue == this.defaultValues[field]) {
      this.changedFields.delete(field)
    }
  }

  getField<K extends keyof T>(key: K): T[K] {
    return this.data[key]
  }

  setField<K extends keyof T>(key: K, value: T[K]): void {
    this.data[key] = value
    this.updateChangedFields(key, value)
  }

  toJSON(): T {
    return Object.fromEntries(
      Object.entries(this.data).filter(([key]) => this.changedFields.has(key)),
    ) as T
  }

  abstract displayRunArgs(): string[]
}

const CLASSIFICATION_KEYS = ['imgSize', 'upsample'] as const
const CLASSIFICATION_DEFAULT_VALUES = {
  imgSize: null as readonly [number, number] | null,
  upsample: DEFAULT_UPSAMPLE,
}
type ClassificationRunArgsKeys = (typeof CLASSIFICATION_KEYS)[number]

const classificationKeyGuard = (key: string): key is ClassificationRunArgsKeys => {
  return CLASSIFICATION_KEYS.includes(key as ClassificationRunArgsKeys)
}

const classificationArrayKeyGuard = (
  key: string,
): key is ArrayKeys<typeof CLASSIFICATION_DEFAULT_VALUES> => {
  return Array.isArray(
    CLASSIFICATION_DEFAULT_VALUES[key as ArrayKeys<typeof CLASSIFICATION_DEFAULT_VALUES>],
  )
}
const classificationRunArgsDisplay = (runArgs: typeof CLASSIFICATION_DEFAULT_VALUES): string[] => {
  return Object.entries(runArgs)
    .filter(([key, value]) => classificationKeyGuard(key) && value !== null)
    .map(([key, value]) =>
      classificationKeyGuard(key)
        ? `${ClassificationRunArgs.displayKeyMap()[key]}: ${classificationArrayKeyGuard(key) && Array.isArray(value) ? value.join(` ${ClassificationRunArgs.joinerMap()[key]} `) : value}`
        : '',
    )
}
class ClassificationRunArgs extends RunArgs<typeof CLASSIFICATION_DEFAULT_VALUES> {
  static keys() {
    return CLASSIFICATION_KEYS
  }

  defaultValues = CLASSIFICATION_DEFAULT_VALUES

  static joinerMap(): Record<ArrayKeys<typeof CLASSIFICATION_DEFAULT_VALUES>, string> {
    return {
      imgSize: 'x',
    }
  }

  static displayKeyMap(): Record<ClassificationRunArgsKeys, string> {
    return {
      imgSize: 'Image Size',
      upsample: 'Oversample',
    }
  }

  protected data = $state(CLASSIFICATION_DEFAULT_VALUES)

  changedFields: SvelteSet<ClassificationRunArgsKeys> = $state(new SvelteSet())

  keyGuard(key: string): key is ClassificationRunArgsKeys {
    return ClassificationRunArgs.keys().includes(key as ClassificationRunArgsKeys)
  }

  arrayKeyGuard(key: string): key is ArrayKeys<typeof CLASSIFICATION_DEFAULT_VALUES> {
    return Array.isArray(this.data[key as ArrayKeys<typeof CLASSIFICATION_DEFAULT_VALUES>])
  }

  displayRunArgs(): string[] {
    return classificationRunArgsDisplay(this.data)
  }
}

const OBJECT_DETECTION_KEYS = [
  ...CLASSIFICATION_KEYS,
  'minAbsBboxSize',
  'minAbsBboxArea',
  'minRelBboxSize',
  'minRelBboxArea',
  'cropRatio',
  'addMaskChannel',
] as const
type ObjectDetectionRunArgsKeys = (typeof OBJECT_DETECTION_KEYS)[number]
const OBJECT_DETECTION_DEFAULT_VALUES = {
  ...CLASSIFICATION_DEFAULT_VALUES,
  minAbsBboxSize: 0,
  minAbsBboxArea: 0,
  minRelBboxSize: 0.0,
  minRelBboxArea: 0.0,
  cropRatio: 1.0,
  addMaskChannel: false,
}
type IObjectDetectionRunArgs = Omit<typeof OBJECT_DETECTION_DEFAULT_VALUES, 'cropRatio'> & {
  cropRatio: number | null
}

const objectDetectionKeyGuard = (key: string): key is ObjectDetectionRunArgsKeys => {
  return ObjectDetectionRunArgs.keys().includes(key as ObjectDetectionRunArgsKeys)
}

const objectDetectionNullMeaningfulKeyGuard = (
  key: string,
): key is keyof typeof ObjectDetectionRunArgs.nullMeaningfulKeys => {
  return ObjectDetectionRunArgs.nullMeaningfulKeys().includes(key)
}

const objectDetectionArrayKeyGuard = (
  runArgs: IObjectDetectionRunArgs,
  key: string,
): key is ArrayKeys<IObjectDetectionRunArgs> => {
  return Array.isArray(runArgs[key as ArrayKeys<IObjectDetectionRunArgs>])
}

const objectDetectionRunArgsDisplay = (runArgs: IObjectDetectionRunArgs): string[] => {
  return Object.entries(runArgs)
    .filter(
      ([key, value]) =>
        objectDetectionKeyGuard(key) &&
        ObjectDetectionRunArgs.keys().includes(key) &&
        (ObjectDetectionRunArgs.nullMeaningfulKeys().includes(key) || value !== null),
    )
    .map(([key, value]) => {
      if (objectDetectionNullMeaningfulKeyGuard(key) && value === null) {
        return ObjectDetectionRunArgs.nullMeaningfulMessages()[key]
      }
      return objectDetectionKeyGuard(key)
        ? `${ObjectDetectionRunArgs.displayKeyMap()[key]}: ${
            objectDetectionArrayKeyGuard(runArgs, key) && Array.isArray(value)
              ? value.join(` ${ObjectDetectionRunArgs.joinerMap()[key]} `)
              : value
          }`
        : ''
    })
}

class ObjectDetectionRunArgs extends RunArgs<IObjectDetectionRunArgs> {
  static keys() {
    return OBJECT_DETECTION_KEYS
  }

  static joinerMap(): Record<ArrayKeys<IObjectDetectionRunArgs>, string> {
    return {
      ...ClassificationRunArgs.joinerMap(),
    }
  }

  static displayKeyMap(): Record<ObjectDetectionRunArgsKeys, string> {
    return {
      ...ClassificationRunArgs.displayKeyMap(),
      minAbsBboxSize: 'Minimum absolute bbox size',
      minAbsBboxArea: 'Minimum absolute bbox area',
      minRelBboxSize: 'Minimum relative bbox size',
      minRelBboxArea: 'Minimum relative bbox area',
      cropRatio: 'Crop ratio',
      addMaskChannel: 'Add mask channel',
    }
  }

  defaultValues = OBJECT_DETECTION_DEFAULT_VALUES

  protected data = $state(OBJECT_DETECTION_DEFAULT_VALUES)

  static nullMeaningfulKeys() {
    return ['cropRatio']
  }

  static nullMeaningfulMessages() {
    return { cropRatio: 'Do not crop the frame' }
  }

  displayRunArgs(): string[] {
    return objectDetectionRunArgsDisplay(this.data)
  }
}

const checkIfObjectDetection = (runArgs: IClassificationRunArgs | IODRunArgs): boolean => {
  let objectDetection = false
  for (const key of new SvelteSet(ObjectDetectionRunArgs.keys()).difference(
    new SvelteSet(ClassificationRunArgs.keys()),
  )) {
    if (key in runArgs) {
      objectDetection = true
      break
    }
  }
  return objectDetection
}

const displayRunArgs = (
  runArgs:
    | ClassificationRunArgs
    | ObjectDetectionRunArgs
    | IClassificationRunArgs
    | IODRunArgs
    | null,
): string[] => {
  if (runArgs instanceof ClassificationRunArgs) {
    return runArgs.displayRunArgs()
  } else if (runArgs instanceof ObjectDetectionRunArgs) {
    return runArgs.displayRunArgs()
  } else if (runArgs) {
    const objectDetection = checkIfObjectDetection(runArgs)
    if (objectDetection) {
      return objectDetectionRunArgsDisplay(runArgs as IObjectDetectionRunArgs)
    } else {
      return classificationRunArgsDisplay(runArgs as IClassificationRunArgs)
    }
  } else {
    return []
  }
}

interface ClassificationIfc {
  getField<K extends keyof typeof CLASSIFICATION_DEFAULT_VALUES>(
    key: K,
  ): (typeof CLASSIFICATION_DEFAULT_VALUES)[K]

  setField<K extends keyof typeof CLASSIFICATION_DEFAULT_VALUES>(
    key: K,
    value: (typeof CLASSIFICATION_DEFAULT_VALUES)[K],
  ): void
}

export type { ClassificationIfc, ClassificationRunArgsKeys, ObjectDetectionRunArgsKeys }
export {
  checkIfObjectDetection,
  ClassificationRunArgs,
  DEFAULT_IMAGE_SIZE,
  DEFAULT_RADAR_SIZE,
  displayRunArgs,
  ObjectDetectionRunArgs,
}
